/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  ComponentType,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Button,
  Form,
  Icon,
  Label,
  Message,
  Segment
} from 'semantic-ui-react'
import RichTextEditor, { EditorValue, ToolbarConfig } from 'react-rte'
import filter from 'lodash/filter'
// Local
import {
  CONDITION,
  shouldRenderReskin2025
} from '../../../constant'
import { cdiExpectedDiagnosisEnabled } from '../../common/currentUserUtils'
import { formatIcd10Code, rawIcd10Code } from '../../common/formatUtils'
import ReasonHandler, { NEW_RULE } from '../condition/reason'
import Icd10Search from '../condition/Icd10Search'
import {
  EncounterConditionDisplay,
  EncounterConditionDisplayPayload,
  EncounterConditionRuleRow,
  User
} from '../../authenticated/types'
import If from '../../common/if'
import ReveleerButton from '../../common/ReveleerButton'
import { RootState } from '../../../store'
import ReveleerCoachingBox from '../../ReveleerCoachingBox'
import { Patient } from '../../patient/patient'

type Rule = {
  id: string | undefined
  ruleType: string
  type: string | undefined
  title: string
  statusCode: keyof typeof CONDITION.STATUS
  reason: string
}

const mapStateToProps = (state: RootState) => ({
  currentUser: state.user
})

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeForm = Form as unknown as ComponentType<any>

const statusOptions = [
  {
    key: CONDITION.TYPE.POTENTIAL,
    value: CONDITION.TYPE.POTENTIAL,
    text: 'Potential'
  },
  {
    key: CONDITION.TYPE.KNOWN,
    value: CONDITION.TYPE.KNOWN,
    text: 'Known'
  },
  {
    key: CONDITION.TYPE.ACTIVITY,
    value: CONDITION.TYPE.ACTIVITY,
    text: 'Activity'
  }
]

type ReduxStateProps = ReturnType<typeof mapStateToProps>

type Props = ReduxStateProps & {
  currentUser: User
  onSave: (condition: Partial<EncounterConditionDisplay>) => void
  patient: Patient
}

function ConditionCreate(
  props: Props
): JSX.Element {
  const {
    currentUser,
    onSave,
    patient
  } = props

  const [
    title,
    setTitle
  ] = useState<string>('')

  const [
    conditionStatus,
    setConditionStatus
  ] = useState<keyof typeof CONDITION.TYPE>(CONDITION.TYPE.POTENTIAL)

  const [
    content,
    setContent
  ] = useState<EditorValue>(RichTextEditor.createValueFromString('', 'markdown'))

  const [
    icd10Code,
    setIcd10Code
  ] = useState<string>('')

  const [
    icd10Desc,
    setIcd10Desc
  ] = useState<string>('')

  const [
    expectedDiagnosisCode,
    setExpectedDiagnosisCode
  ] = useState<string>('')

  const [
    expectedDiagnosisDesc,
    setExpectedDiagnosisDesc
  ] = useState<string>('')

  const [
    rules,
    setRules
  ] = useState<Rule[]>([])

  const [
    error,
    setError
  ] = useState<any | null>(null)

  const toolbarConfig = () => (
    {
      display: ["INLINE_STYLE_BUTTONS", "BLOCK_TYPE_BUTTONS"],
      INLINE_STYLE_BUTTONS: [
        { label: "Bold", style: "BOLD", className: "custom-css-class" },
        { label: "Italic", style: "ITALIC" },
        { label: "Underline", style: "UNDERLINE" }
      ],
      BLOCK_TYPE_BUTTONS: [
        { label: "UL", style: "unordered-list-item" },
        { label: "OL", style: "ordered-list-item" }
      ]
    }
  )

  const onChangeContent = (
    contentProp: EditorValue
  ): void => {
    setContent(contentProp)
  }

  const onChangeValue = (
    e: any,
    { name, value }: any
  ) => {
    if (name === 'conditionStatus') {
      setConditionStatus(value as keyof typeof CONDITION.TYPE)
    }
    if (name === 'content') {
      setContent(value as EditorValue)
    }
    if (name === 'rules') {
      setRules(value as Rule[])
    }
    if (name === 'title') {
      setTitle(value as string)
    }
    if (name === 'icd10Code') {
      setIcd10Code(value as string)
    }
    if (name === 'icd10Desc') {
      setIcd10Desc(value as string)
    }
    if (name === 'expectedDiagnosisCode') {
      setExpectedDiagnosisCode(value as string)
    }
    if (name === 'expectedDiagnosisDesc') {
      setExpectedDiagnosisDesc(value as string)
    }
    if (name === 'error') {
      setError(value)
    }
  }

  const onSelectConditionStatus = (
    e: any,
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    { name, value }: Record<string, keyof typeof CONDITION.TYPE>
  ) => {
    setConditionStatus(value)
    if (value !== CONDITION.TYPE.POTENTIAL) {
      setExpectedDiagnosisCode('')
      setExpectedDiagnosisDesc('')
    }
  }

  const onSelectIcd10 = (
    result: {
      id: string
      description: string
    }
  ) => {
    setIcd10Code(result.id)
    setIcd10Desc(result.description)
    setTitle(result.description)
  }

  const onSelectExpectedDiagnosis = (result: any) => {
    const code = result && result.id && result.id.length > 0
      ? result.id
      : null
    setExpectedDiagnosisCode(code as string)
    setExpectedDiagnosisDesc(result.description as string)
  }

  const onClickClear = () => {
    setTitle('')
    setConditionStatus('POTENTIAL')
    setContent(RichTextEditor.createValueFromString('', 'markdown'))
    setIcd10Code('')
    setIcd10Desc('')
    setExpectedDiagnosisCode('')
    setExpectedDiagnosisDesc('')
    setRules([])
    setError(null)
  }

  const onClickSave = () => {
    if (!title || title === '') {
      setError({
        field: 'title',
        description: 'Each Entry Must Have a Title'
      })
      return
    }

    const icd10Json = icd10Code && icd10Code.length > 0
      ? [ rawIcd10Code(icd10Code) ]
      : null

    const cdiTags = {} as any

    if (expectedDiagnosisCode && expectedDiagnosisCode.length > 0) {
      cdiTags.expectedDiagnosis = expectedDiagnosisCode
    }

    const conditionRules: Partial<EncounterConditionRuleRow>[] = filter(rules, rule => {
      // filter out new rules with empty reasons
      if (rule.type === NEW_RULE) {
        return !(rule.reason.length === 0 || !rule.reason.trim())
      }
      return true
    }).map(rule => {
      let revisedRule = {}
      if (rule.type === NEW_RULE) {
        revisedRule = {
          ...rule,
          id: null,
          type: null,
          title: rule.reason,
          statusCode: CONDITION.STATUS.ACTIVE
        }
      }
      if (rule.reason.length === 0 || !rule.reason.trim()) {
        revisedRule = {
          ...rule,
          statusCode: CONDITION.STATUS.REMOVED
        }
      }
      return revisedRule
    })

    const condition: EncounterConditionDisplayPayload = {
      status: CONDITION.STATUS.ACTIVE,
      conditionCode: CONDITION.CODE.OPEN,
      conditionStatus,
      icd10CodeJson: icd10Json,
      title,
      content: content.toString('markdown'),
      addressedDate: null,
      hccVersion: patient.rulesHccVersion,
      priority: 0,
      rules: conditionRules as EncounterConditionRuleRow[],
      cdiTags,
      clinicalEvidence: []
    }
    onSave(condition as Partial<EncounterConditionDisplayPayload>)
  }

  const showExpectedDiagnosis = (
    cdiExpectedDiagnosisEnabled(currentUser) &&
    conditionStatus === CONDITION.TYPE.POTENTIAL
  )

  const key = (new Date()).getTime()

  const color = shouldRenderReskin2025
    ? undefined
    : 'blue'

  const addConditionClassName = shouldRenderReskin2025
    ? 'secondary reskin'
    : 'condition-fields'

  return (
    <SafeSegment attached>
      <If isTrue={ !shouldRenderReskin2025 }>
        <Message>
          <Icon name="info circle" />
          Add Conditions (HCC or non-HCC, Activity or Care Gap)
        </Message>
      </If>
      <If isTrue={ shouldRenderReskin2025 }>
        <ReveleerCoachingBox
          coachingBoxType="infoPositive"
          isShowing
          message={
            <p>
              Add Conditions (HCC or non-HCC, Activity or Care Gap)
            </p>
          }
        />
      </If>
      <SafeForm error={ error !== null }>
        <If isTrue={ error !== null }>
          <If isTrue={ !shouldRenderReskin2025 }>
            <Message error>
              { error && error.description }
            </Message>
          </If>
          <If isTrue={ shouldRenderReskin2025 }>
            <ReveleerCoachingBox
              coachingBoxType="error"
              isShowing
              message={
                <p>
                  { error && error.description }
                </p>
              }
            />
          </If>
        </If>
        <Form.Group widths="equal">
          <Form.Input
            name="title"
            label="Condition Title"
            error={ error !== null && error.field === 'title' }
            placeholder="Title"
            value={ title }
            onChange={ onChangeValue }
          />
          <span className="field">
            <label>ICD10 Code</label>
            <Icd10Search
              key={ key }
              value={ icd10Code }
              onSelect={ onSelectIcd10 }
            />
          </span>
          <Form.Dropdown fluid selection
            label="Condition Status"
            name="conditionStatus"
            placeholder="Condition Status"
            onChange={ onSelectConditionStatus }
            options={ statusOptions }
            value={ conditionStatus }
          />
        </Form.Group>
        <If isTrue={ !!icd10Code }>
          <div
            className={
              shouldRenderReskin2025
                ? 'icd10code-container'
                : 'm1-top m1-bottom'
            }
          >
            <Label
              className={
                shouldRenderReskin2025
                  ? 'reskin'
                  : ''
              }
              pointing={
                shouldRenderReskin2025
                  ? undefined
                  : 'right'
              }
            >
              ICD10 Code
            </Label>
            <span>
              { formatIcd10Code(icd10Code) }
            </span>
            <span>,&nbsp;</span>
            <span className="italic">
              { icd10Desc }
            </span>
          </div>
        </If>
        <span className="field">
          <label>Reasons</label>
          <ReasonHandler
            rules={ rules || [] }
            onChangeValue={ onChangeValue }
          />
        </span>
        <span className="field">
          <label>Content</label>
          <RichTextEditor
            value={ content }
            onChange={ onChangeContent }
            toolbarConfig={ toolbarConfig() as ToolbarConfig }
          />
        </span>
        <If isTrue={ showExpectedDiagnosis }>
          <SafeSegment>
            <span className="field">
              <label>Expected Diagnosis</label>
              <Icd10Search
                key={ key }
                value={ expectedDiagnosisCode }
                onSelect={ onSelectExpectedDiagnosis }
                placeholder="Expected Diagnosis" />
            </span>
            <div className="m-1-top">
              <If isTrue={ !!expectedDiagnosisCode }>
                <div>
                  <Label
                    pointing="right"
                  >
                    Expected Diagnosis Code
                  </Label>
                  <span>&nbsp;{ formatIcd10Code(expectedDiagnosisCode) }</span>
                  <span>,&nbsp;{ expectedDiagnosisDesc }</span>
                </div>
              </If>
              <div className="m-1-top italic">
                An expected diagnosis code associated with this condition
                for the purpose of reporting. This diagnosis code is not
                displayed to the practitioner or coder
              </div>
            </div>
          </SafeSegment>
        </If>
        <div className="m-1-top text-align-right">
          <span>
            <If isTrue={ !shouldRenderReskin2025 }>
              <Button
                id="condition-create-add-condition"
                color={ color }
                className={ addConditionClassName }
                onClick={ onClickSave }>
                Add Condition
              </Button>
            </If>
            <If isTrue={ shouldRenderReskin2025 }>
              <ReveleerButton
                buttonLabel="Add Condition"
                buttonSize="small"
                buttonType="primary"
                id="condition-create-add-condition"
                isDisabled={ false }
                onClick={ onClickSave }
                type="button"
              />
            </If>
          </span>
          <span className="m-1-left">
            <If isTrue={ !shouldRenderReskin2025 }>
              <Button
                id="condition-create-clear"
                className="condition-fields"
                onClick={ onClickClear }
              >
                Clear
              </Button>
            </If>
            <If isTrue={ shouldRenderReskin2025 }>
              <ReveleerButton
                buttonLabel="Clear"
                buttonSize="small"
                buttonType="secondary"
                id="condition-create-clear"
                isDisabled={ false }
                onClick={ onClickClear }
                type="button"
              />
            </If>
          </span>
        </div>
      </SafeForm>
    </SafeSegment>
  )
}

export default connect(mapStateToProps)(ConditionCreate)
